/**
 * AskArloContext.js - Open/close state for the "What should I watch?" panel.
 *
 * The two entry points (header pill, search no-results escape hatch) live in
 * different trees, so panel state is a context. useAskArloPanel() is safe
 * without a provider — it returns disabled defaults — so consumers never
 * need to know whether the feature is configured or mounted.
 *
 * Chat is signed-in only. Both entry points already hide themselves on
 * `enabled`, so gating it here is the whole gate: a signed-out visitor never
 * sees the pill, and Arlo never learns anything about someone who has no
 * account and never agreed to a taste profile.
 */
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { clientEnv } from '../buildTimeEnv';
import arloService from '../services/ArloService';
import { resolveCustomerId } from '../utils/arloIdentity';

const noop = () => {};
const DISABLED_PANEL = {
  enabled: false,
  isOpen: false,
  prefill: null,
  openPanel: noop,
  closePanel: noop,
  clearPrefill: noop,
};

const AskArloContext = createContext(null);

export const useAskArloPanel = () => useContext(AskArloContext) || DISABLED_PANEL;

export const AskArloProvider = ({ children }) => {
  const { isAuthenticated } = useAuth0();
  const enabled = Boolean(clientEnv.REACT_APP_ARLO_PUBLISHABLE_KEY) && isAuthenticated;
  const [isOpen, setIsOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);

  const openPanel = useCallback(
    initialQuery => {
      if (!enabled) return;
      const customerId = resolveCustomerId();
      // isAuthenticated can lead the token by a tick; without a customerId
      // there is nothing to key chat on, so don't open a panel that can't answer.
      if (!customerId) return;

      const trimmed = typeof initialQuery === 'string' ? initialQuery.trim() : '';
      setPrefill(trimmed || null);
      setIsOpen(true);
      // The embed may not exist yet (no rails call on this page) — create it so
      // chat_opened has an instance to buffer into.
      arloService.ensureEmbed(customerId);
      arloService.track('chat_opened');
    },
    [enabled],
  );

  const closePanel = useCallback(() => setIsOpen(false), []);
  const clearPrefill = useCallback(() => setPrefill(null), []);

  const value = useMemo(
    () => ({ enabled, isOpen, prefill, openPanel, closePanel, clearPrefill }),
    [enabled, isOpen, prefill, openPanel, closePanel, clearPrefill],
  );

  return <AskArloContext.Provider value={value}>{children}</AskArloContext.Provider>;
};
